// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { formatMeritLadder, formatMerits } from '@/features/game-definitions/merits';
import { t } from '@/i18n';
import appColors from '@/theme/colors';

export type MeritDistributionEntry = {
  gameId: string;
  name: string;
  ladder: number[];
};

type Props = {
  entries: MeritDistributionEntry[];
};

const SEGMENT_COLORS = ['#A1D493', '#F2C94C', '#5DCAA5', '#F0997B', '#85B7EB', '#ED93B1'];

export default function MeritDistributionBar({ entries }: Props) {
  const withMerits = entries.filter((entry) => entry.ladder.length > 0);
  if (withMerits.length < 2) {
    return null;
  }

  const winnerMerits = withMerits.map((entry) => entry.ladder[0]);
  const totalWinnerMerits = winnerMerits.reduce((sum, merits) => sum + merits, 0);
  if (totalWinnerMerits <= 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>{t('selectGames.meritDistributionTitle')}</Text>
        <Text style={styles.total}>
          {t('selectGames.meritDistributionTotal', { merits: formatMerits(totalWinnerMerits) })}
        </Text>
      </View>
      <View style={styles.bar}>
        {withMerits.map((entry, index) => (
          <View
            key={entry.gameId}
            style={[
              styles.segment,
              {
                flex: winnerMerits[index] || 0.0001,
                backgroundColor: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
              },
            ]}
          />
        ))}
      </View>
      <View style={styles.legend}>
        {withMerits.map((entry, index) => (
          <View key={entry.gameId} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: SEGMENT_COLORS[index % SEGMENT_COLORS.length] }]} />
            <Text style={styles.legendName} numberOfLines={1}>
              {entry.name}
            </Text>
            <Text style={styles.legendLadder}>{formatMeritLadder(entry.ladder, '/')}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: appColors.background.panel,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
  },
  title: {
    fontSize: 12,
    color: appColors.text.muted,
    fontWeight: '700',
  },
  total: {
    fontSize: 12,
    color: appColors.accent.gold,
    fontWeight: '700',
  },
  bar: {
    flexDirection: 'row',
    height: 10,
    borderRadius: 5,
    overflow: 'hidden',
    gap: 2,
  },
  segment: {
    height: '100%',
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 16,
    rowGap: 4,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendName: {
    fontSize: 11,
    color: appColors.text.secondary,
    maxWidth: 140,
  },
  legendLadder: {
    fontSize: 11,
    fontWeight: '700',
    color: appColors.text.primary,
  },
});
